import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Button } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useTranslation } from 'react-i18next'

export interface SimpleRecorderResult {
  audioBlob: Blob | null
  audioURL: string | null
  isRecording: boolean
  startRecording: () => Promise<void>
  stopRecording: () => void
  reset: () => void
}

export function useSimpleRecorder(): SimpleRecorderResult {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioURL, setAudioURL] = useState<string | null>(null)
  const [isRecording, setIsRecording] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const urlRef = useRef<string | null>(null)

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
      }
    }
  }, [])

  const startRecording = useCallback(async () => {
    // Clean up previous recording
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }
    setAudioBlob(null)
    setAudioURL(null)
    chunksRef.current = []

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    streamRef.current = stream

    const recorder = new MediaRecorder(stream)
    mediaRecorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data)
      }
    }

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
      const url = URL.createObjectURL(blob)
      urlRef.current = url
      setAudioBlob(blob)
      setAudioURL(url)
      setIsRecording(false)
      // Stop all tracks
      stream.getTracks().forEach((track) => track.stop())
    }

    recorder.start()
    setIsRecording(true)
  }, [])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }, [])

  const reset = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setAudioBlob(null)
    setAudioURL(null)
    setIsRecording(false)
    chunksRef.current = []
  }, [])

  return { audioBlob, audioURL, isRecording, startRecording, stopRecording, reset }
}

export interface FileUploaderProps {
  onFileSelected: (blob: Blob, url: string) => void
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onFileSelected }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const url = URL.createObjectURL(file)
      onFileSelected(file, url)
      // Reset so same file can be re-selected
      if (inputRef.current) inputRef.current.value = ''
    },
    [onFileSelected]
  )

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={() => inputRef.current?.click()}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          borderColor: 'rgba(0, 207, 243, 0.4)',
          color: '#007d8a',
          '&:hover': {
            borderColor: '#007d8a',
            backgroundColor: 'rgba(0, 207, 243, 0.08)'
          }
        }}
      >
        {t('tts.uploadFile', 'Upload File')}
      </Button>
    </>
  )
}
